import React, { useState, forwardRef } from 'react';
import styled from 'styled-components';
import katex from 'katex';

import { Datum, IGraphData } from './types';
import { IMargins } from './VerticalLine';

const LegendDiv = styled.div<{ margins: IMargins; fullWidth: boolean }>`
    padding-left: ${({ margins: { left } }) => `${left}px`};
    padding-right: ${({ margins: { left } }) => `${left}px`};
    > ul {
        list-style-type: none;
        padding: 0px;
        margin: 0px;
        overflow: hidden;

        > li {
            padding-right: 10px;
            float: left;
            width: ${({ fullWidth }) => `${fullWidth ? '100%' : '50%'}`};
        }
    }
`;
const LineGraphLegendLi = styled.li`
    cursor: pointer;
    opacity: 0.5;
    display: flex;
    &.active {
        opacity: 1;
    }
    flex: 1;
`;
const Swatch = styled.div<{ color: string }>`
    background-color: ${({ color }) => color};
    width: 1.5em;
    max-width: 1.5em;
    height: 0.8em;
    display: inline-block;
    margin-right: 0.2em;
    flex: 1 0 auto;
    margin-top: 7px;
`;
const LegendName = styled.span<{ fontSize: number }>`
    display: inline-block;
    padding-bottom: 0;
    font-size: ${({ fontSize }) => (fontSize / 100) * 0.8}rem;
`;
const UnorderedList = styled.ul`
    display: flex;
`;

interface IProps {
    id?: string;
    fontSize: number;
    indexesToFilter: number[];
    updateIndexesToFilter(i: number[]): void;
    selectedDatapoints: number[];
    margins: IMargins;
    legendFullWidth: boolean;
    data: IGraphData[];
}

export default forwardRef(
    ({ data, indexesToFilter, margins, id, updateIndexesToFilter, fontSize, selectedDatapoints, legendFullWidth }: IProps, ref: React.Ref<HTMLDivElement>) => {
        const DEFAULT_DP = 4;
        const [filteredLegend, setFilteredLegend] = useState<number[]>([]);

        const onFilterData = (i: number) => {
            if (indexesToFilter.length === 0) {
                setFilteredLegend([i]);
                updateIndexesToFilter([i]);
            } else {
                const newIndexesToFilter = indexesToFilter.includes(i) ? indexesToFilter.filter((key) => key !== i) : [...indexesToFilter, i];
                setFilteredLegend(newIndexesToFilter);
                updateIndexesToFilter(newIndexesToFilter);
            }
        };

        interface getOnSelectPointLabelProps {
            selectedDataPoint: Datum;
            decimalPointValue: number;
            displayUnit: string;
            label: string;
        }

        const Name = ({ selectedDataPoint, decimalPointValue, displayUnit, label }: getOnSelectPointLabelProps) => {
            const dataPointValue = selectedDataPoint && selectedDataPoint.y ? selectedDataPoint.y.toFixed(decimalPointValue) : '';
            const dataPointLabelTime = selectedDataPoint ? `@ ${selectedDataPoint.timeX}` : '';
            if (!dataPointValue && !displayUnit && !dataPointLabelTime) {
                return (
                    <div className={'No dataPointValue'} style={{ textAlign: 'left' }}>
                        <span>{label} </span>
                    </div>
                );
            }

            return (
                <div style={{ textAlign: 'left' }}>
                    <span>{label} </span>
                    <span style={{ whiteSpace: 'nowrap' }}>
                        ({dataPointValue}
                        <span dangerouslySetInnerHTML={{ __html: katex.renderToString(displayUnit) }} />
                        {`${dataPointLabelTime ? ' ' + dataPointLabelTime : ''}`})
                    </span>{' '}
                </div>
            );
        };

        return (
            <LegendDiv ref={ref} className={`Legend`} id={id} margins={margins} fullWidth={legendFullWidth}>
                <UnorderedList>
                    {data.map(({ color, data: dataPoints, dp, label, displayUnit }, i) => {
                        const pointsToSelectFrom = dataPoints[0];
                        const selectedDataPoint = selectedDatapoints && (pointsToSelectFrom[selectedDatapoints[i]] as Datum);
                        const decimalPointValue = dp != undefined ? dp : DEFAULT_DP;

                        return (
                            <LineGraphLegendLi
                                key={`linegraph-legend-${i}`}
                                onClick={() => onFilterData(i)}
                                className={filteredLegend.includes(i) ? '' : 'active'}
                            >
                                <Swatch className='swatch' color={color} />
                                <LegendName fontSize={fontSize}>
                                    <Name selectedDataPoint={selectedDataPoint} decimalPointValue={decimalPointValue} displayUnit={displayUnit} label={label} />
                                </LegendName>
                            </LineGraphLegendLi>
                        );
                    })}
                </UnorderedList>
            </LegendDiv>
        );
    }
);
